import {v4 as uuidv4} from 'uuid';
import {Injectable} from '@angular/core';
import MapboxDraw from '@mapbox/mapbox-gl-draw';

import {ToolTypes} from 'src/app/constants/map';
import {MapboxGlService} from 'src/app/services/mapbox-gl.service';
import * as turf from '@turf/turf';
import * as numeral from 'numeral';
import {MatDialogRef} from '@angular/material/dialog';
import {MapboxglDrawFeatureComponent} from './mapboxgl-draw-feature.component';

@Injectable({
    providedIn: 'root'
})
export class MapboxglDrawFeatureControl {

    protected draw: any;
    private container: any;
    private randomIds: any[] = [];
    private service: MapboxGlService;
    public lineButton: HTMLButtonElement;
    public polygonButton: HTMLButtonElement;
    private trashButton: HTMLButtonElement;
    private lineButtonIcon: any;
    private isLineButtonActive = false;
    private polygonButtonIcon: any;
    private isPolygonButtonActive = false;
    private trashButtonIcon: any;
    private checkUpdate = false;
    private logOpen: boolean;
    public dialogRef: MatDialogRef<any> = null;

    constructor(
        {mapboxglService}
    ) {

        this.eventLineClick = this.eventLineClick.bind(this);
        this.eventTrashClick = this.eventTrashClick.bind(this);
        this.eventPolygonClick = this.eventPolygonClick.bind(this);

        this.service = mapboxglService;
        this.service.drawFeatureSubject.subscribe(e => {
            this.updateArea(e).then();
            this.updateDrawResultLayer(e);
        });
        this.draw = new MapboxDraw({
            displayControlsDefault: false,
        });
        this.service.map.addControl(this.draw);
        this.checkControl();
        this.drawRender();
    }

    checkControl(): void {
        this.service.controlOnLoadedSubject.subscribe(control => {
            if (control !== ToolTypes.draw_line_string) {
                if (this.lineButton) {
                    this.isLineButtonActive = false;
                    this.lineButtonIcon.style.color = 'black';
                    this.lineButton.classList.remove('active');
                }
            }
            if (control !== ToolTypes.draw_polygon) {
                if (this.polygonButton) {
                    this.isPolygonButtonActive = false;
                    this.polygonButtonIcon.style.color = 'black';
                    this.polygonButton.classList.remove('active');
                }
            }
            if (control !== ToolTypes.draw_remove) {
                if (this.trashButton) {
                    this.trashButton.style.color = 'black';
                    this.trashButton.classList.remove('active');
                }
                if (this.dialogRef) {
                    this.dialogRef.close();
                }
            }
            try {
                if (control !== ToolTypes.draw_line_string && control !== ToolTypes.draw_polygon && this.draw && this.draw.getMode()) {
                    this.draw.changeMode(ToolTypes.simple_select);
                }
            } catch (error) {
            }
        });
    }

    drawRender(): void {
        // Create a div to display the distance
        const distanceTooltip = document.createElement('div');
        distanceTooltip.classList.add('distance-tooltip');
        distanceTooltip.style.position = 'absolute';
        distanceTooltip.style.pointerEvents = 'none';
        distanceTooltip.style.padding = '4px 8px';
        distanceTooltip.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
        distanceTooltip.style.color = '#fff';
        distanceTooltip.style.borderRadius = '4px';
        distanceTooltip.style.fontSize = '12px';
        // Render draw
        this.service.map.on('draw.render', (event: any) => {
            this.checkToolTypeWhenRenderDraw(distanceTooltip, event);
        });
    }

    checkToolTypeWhenRenderDraw(distanceTooltip: any, event: any): void {
        if (this.draw.getMode() === ToolTypes.draw_line_string || this.draw.getMode() === ToolTypes.draw_polygon) {
            if (this.checkUpdate === true) {
                if (distanceTooltip.parentElement) {
                    this.service.map.getCanvasContainer().removeChild(distanceTooltip);
                }
                this.service.map.off('draw.render', event);
                if (this.draw.getMode() === ToolTypes.draw_line_string) {
                    this.checkIsLineButtonActive();
                }
                if (this.draw.getMode() === ToolTypes.draw_polygon) {
                    this.checkIsPolygonButtonActive();
                }
            }
            if (this.checkUpdate === false) {
                if (distanceTooltip.parentElement) {
                    this.service.map.getCanvasContainer().removeChild(distanceTooltip);
                }
                if (!distanceTooltip.parentElement) {
                    this.service.map.getCanvasContainer().appendChild(distanceTooltip);
                }
                if (this.draw.getMode() === ToolTypes.draw_line_string) {
                    this.updateDistanceFromStartPointToCursorPosition(distanceTooltip);
                }
                if (this.draw.getMode() === ToolTypes.draw_polygon) {
                    this.updatePolygonAreaAtCursorPosition(distanceTooltip);
                }
                this.service.map.off('draw.render', event);
            }
        }
        if (this.draw.getMode() === ToolTypes.simple_select) {
            if (distanceTooltip.parentElement) {
                this.service.map.getCanvasContainer().removeChild(distanceTooltip);
            }
            this.service.map.off('draw.render', event);
        }
    }

    updateDistanceFromStartPointToCursorPosition(distanceTooltip: any): void {
        const map = this.service.map;
        let distance = 0;
        const all = this.draw.getAll();
        let label = '';
        // Khai báo feature
        let feature = all.features[0];
        if (all.features.length > 1) {
            feature = all.features[all.features.length - 1];
        }
        // Biến lưu trữ điểm đầu tiên
        let startPoint = null;
        // Sự kiện click chuột
        this.service.map.on('click', (e: any) => {
            // Lưu trữ điểm bắt đầu mới
            startPoint = e.lngLat.toArray();
        });
        this.service.map.on('dblclick', (e: any) => {
            // Xử lý sự kiện double click tại đây
            // Thực hiện các hành động khác, ví dụ: hoàn thành việc vẽ
            if (startPoint !== null) {
                startPoint = null;
            }
        });
        // Sự kiện di chuyển chuột
        this.service.map.on('mousemove', (e: any) => {
            if (this.isLineButtonActive === false && this.isPolygonButtonActive === false) {
                map.off('mousemove', (e));
                return;
            }
            if (turf.getType(feature) === 'LineString') {
                if (startPoint && feature.geometry.type === 'LineString' && feature.geometry.type !== 'Polygon') {
                    // Tính khoảng cách từ điểm đầu tiên đến vị trí hiện tại của chuột
                    const currentPoint = e.lngLat.toArray();
                    distance = turf.distance(startPoint, currentPoint);
                    label = `${numeral(distance).format('0,0.0a')}` + ` km<br/>` + `Click to continue drawing<br/>` + `Double click to finish`;
                    // Tính toán điểm đích dựa trên khoảng cách và góc phương vị
                    const bearing = turf.bearing(startPoint, currentPoint);
                    const destPoint = turf.destination(startPoint, distance, bearing);
                    // Hiển thị khoảng cách
                    // Update position and value of div
                    distanceTooltip.style.left = `${e.point.x}px`;
                    distanceTooltip.style.top = `${e.point.y}px`;
                    distanceTooltip.innerHTML = label.replace('\n', '<br>');
                }
            }
        });
    }

    updatePolygonAreaAtCursorPosition(distanceTooltip: any): void {
        const map = this.service.map;
        let area = 0;
        let label = '';
        // Khai báo feature
        const all = this.draw.getAll();
        this.service.map.on('mousemove', (e) => {
            if (this.isLineButtonActive === false && this.isPolygonButtonActive === false) {
                map.off('mousemove', (e));
                return;
            }
            all.features.forEach(feature => {
                if (feature.geometry.type === 'Polygon') {
                    if (feature.geometry.type === 'Polygon' &&
                        feature.geometry.type !== 'LineString' &&
                        feature.geometry.coordinates.length > 0 &&
                        feature.geometry.coordinates[0].length > 3) {
                        area = turf.area(feature.geometry);
                        label = `${numeral(area).format('0,0.0a')}` + ` km²<br/>` + `Click to continue drawing<br/>` + `Double click to finish`;
                    }
                    // Update position and value of div
                    distanceTooltip.style.left = `${e.point.x}px`;
                    distanceTooltip.style.top = `${e.point.y}px`;
                    distanceTooltip.innerHTML = label.replace('\n', '<br>');
                }
            });
        });
    }

    updateDrawResultLayer(e): void {
        const layers = this.service.map.getStyle().layers;
        const feature: any = e.features && e.features.length ? e.features[0] : null;
        for (const item of this.randomIds) {
            if (item.featureId === feature.id) {
                if (layers.some(layer => layer.id === item.resultId)) {
                    this.service.map.removeLayer(item.resultId);
                }
            }
        }
        this.updateArea(e).then();
    }

    eventLineClick(e): void {
        this.lineButton = e.currentTarget;
        this.lineButtonIcon = this.lineButton.querySelector<HTMLElement>('.material-icons-outlined');
        this.checkIsLineButtonActive();
    }

    checkIsLineButtonActive(): void {
        if (!this.isLineButtonActive) {
            this.isLineButtonActive = true;
            this.lineButtonIcon.style.color = 'red';
            this.lineButton.classList.add('active');
            this.draw.changeMode(ToolTypes.draw_line_string);
            this.service.controlOnLoadedSubject.next(ToolTypes.draw_line_string);
            this.checkUpdate = false;
        } else if (this.isLineButtonActive) {
            this.draw.changeMode(ToolTypes.simple_select);
            this.isLineButtonActive = false;
            this.lineButton.classList.remove('active');
            this.lineButtonIcon.style.color = 'black';
        }
    }

    eventPolygonClick(e): void {
        this.polygonButton = e.currentTarget;
        this.polygonButtonIcon = this.polygonButton.querySelector<HTMLElement>('.material-icons-outlined');
        this.checkIsPolygonButtonActive();
    }

    checkIsPolygonButtonActive(): void {
        if (!this.isPolygonButtonActive) {
            this.isPolygonButtonActive = true;
            this.polygonButtonIcon.style.color = 'red';
            this.polygonButton.classList.add('active');
            this.draw.changeMode(ToolTypes.draw_polygon);
            this.service.controlOnLoadedSubject.next(ToolTypes.draw_polygon);
            this.checkUpdate = false;
        } else if (this.isPolygonButtonActive) {
            this.draw.changeMode(ToolTypes.simple_select);
            this.isPolygonButtonActive = false;
            this.polygonButton.classList.remove('active');
            this.polygonButtonIcon.style.color = 'black';
        }
    }

    eventTrashClick(e): void {
        this.trashButton = e.currentTarget;
        this.trashButtonIcon = this.trashButton.querySelector<HTMLElement>('.material-icons-outlined');
        if (!this.logOpen) {
            this.trashButton.classList.add('active');
            this.trashButtonIcon.style.color = 'red';
            this.service.controlOnLoadedSubject.next(ToolTypes.draw_remove);
            this.openDialog();
        } else if (this.logOpen) {
            this.trashButton.classList.remove('active');
            this.trashButtonIcon.style.color = 'black';
            this.dialogRef.close();
            this.logOpen = false;
        }
    }

    openDialog(): void {
        const dialogWidth = 174;
        if (this.trashButton.classList.contains('active')) {
            this.trashButton.classList.remove('active');
            this.logOpen = true;
        }
        const offsetTop = document.querySelector<HTMLElement>('.mapbox-gl-custom_draw').getBoundingClientRect().top;
        const offsetLeft = document.querySelector<HTMLElement>('.mapbox-gl-custom_draw').getBoundingClientRect().left;
        this.dialogRef = this.service.dialog.open(MapboxglDrawFeatureComponent, {
            height: 'auto',
            width: `${dialogWidth}px`,
            hasBackdrop: false,
        });
        let dialog = document.querySelector<HTMLElement>('.mat-dialog-container');
        dialog.style.padding = '0';
        dialog.style.backgroundColor = 'transparent';
        dialog.style.boxShadow = 'none';
        dialog.style.borderRadius = '25px';
        this.dialogRef.updatePosition({
            top: `${offsetTop + 62}px`,
            left: `${offsetLeft - dialogWidth - 5}px`
        });
        this.dialogRef.afterClosed().subscribe(() => {
            this.logOpen = false;
            this.trashButton.classList.remove('active');
            this.trashButtonIcon.style.color = 'black';
        });
        const deleteAllButton = document.querySelector<any>('.button-2');
        deleteAllButton.addEventListener('click', () => {
            this.removeItems();
            dialog = null;
            this.dialogRef.close();
        });
    }

    async updateArea(e): Promise<void> {
        let unit = ' km';
        let result = null;
        let symbolPlacement = null;
        const feature: any = e.features && e.features.length ? e.features[0] : null;
        if (feature && this.draw) {
            if (this.draw.getMode() === ToolTypes.draw_polygon) {
                unit = ' km²';
                result = await this.service.commonService.tinhDienTich(feature);
                symbolPlacement = 'point';
            }
            if (this.draw.getMode() === ToolTypes.draw_line_string) {
                result = await this.service.commonService.tinhChieuDai(feature);
                symbolPlacement = 'line';
            }
            if (this.draw.getMode() === ToolTypes.simple_select) {
                if (feature.geometry.type === 'Polygon') {
                    unit = ' km²';
                    result = await this.service.commonService.tinhDienTich(feature);
                    symbolPlacement = 'point';
                }
                if (feature.geometry.type === 'LineString') {
                    unit = ' km';
                    result = await this.service.commonService.tinhChieuDai(feature);
                    symbolPlacement = 'line';
                }
            }
            if (result && result.status) {
               this.createDrawResultLayer(feature, result, symbolPlacement, unit);
            }
        }
        if (this.lineButton && this.lineButton.classList.contains('active')) {
            this.draw.changeMode(ToolTypes.draw_line_string);
            this.isPolygonButtonActive = false;
            result = 0;
        }
        if (this.polygonButton && this.polygonButton.classList.contains('active')) {
            this.draw.changeMode(ToolTypes.draw_polygon);
            this.isLineButtonActive = false;
            result = 0;
        }
    }

    createDrawResultLayer(feature: any, result: any, symbolPlacement: any, unit: any): void {
        this.draw.changeMode(ToolTypes.simple_select);
        this.checkUpdate = true;
        const resultId = uuidv4();
        const featureId = feature.id;
        this.randomIds.push({featureId, resultId});
        feature.properties.value = result.data.toLocaleString('en') + unit;
        const geojson = {
            type: 'geojson',
            data: feature
        };
        this.service.map.addSource(resultId, geojson);
        this.service.map.addLayer({
            id: resultId,
            type: 'symbol',
            source: resultId,
            minzoom: 1,
            maxzoom: 24,
            layout: {
                'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
                'text-size': 15,
                'symbol-placement': symbolPlacement,
                'text-allow-overlap': true,
                'text-field': '{value}',
                'text-offset': [0, 0.6],
                'text-anchor': 'top'
            },
            paint: {
                'text-color': '#000',
                'text-halo-color': 'white',
                'text-halo-width': 2,
                'text-halo-blur': 1
            },
        });
    }

    removeItems(): void {
        this.draw.deleteAll();
        const layers = this.service.map.getStyle().layers;
        for (const item of this.randomIds) {
            if (layers.some(layer => layer.id === item.resultId)) {
                this.service.map.removeLayer(item.resultId);
                this.service.map.removeSource(item.resultId);
            }
        }
    }

    onAdd(): any {
        const drawLineTool = this.service.utilityFn.createTool({
            name: 'Line tool',
            iconClass: 'material-icons-outlined',
            code: 'DRAW_LINE',
            active: false,
            className: 'timeline'
        }, this.eventLineClick);
        const drawPolygonTool = this.service.utilityFn.createTool({
            name: 'Polygon tool',
            iconClass: 'material-icons-outlined',
            code: 'DRAW_POLYGON',
            active: false,
            className: 'pentagon'
        }, this.eventPolygonClick);
        const trashTool = this.service.utilityFn.createTool({
            name: 'Trash tool',
            iconClass: 'material-icons-outlined',
            code: 'TRASH',
            active: false,
            className: 'delete'
        }, this.eventTrashClick);
        this.container = this.service.utilityFn.createControl([drawLineTool, drawPolygonTool, trashTool], 'mapbox-gl-custom_draw');
        return this.container;
    }

    onRemove(): void {
        this.container.parentNode.removeChild(this.container);
    }

}
